export type TUCapacityDimension =
  | 'ingress'
  | 'egress'
  | 'requestEntries'
  | 'topicsOrPartitions'
  | 'clientSessions'

export type TUCapacityUsage = Record<TUCapacityDimension, number>

export interface TUCapacitySpec {
  mode?: string
  minTU?: number
  maxTU?: number
  timeZone?: string
  schedules?: Array<{
    name?: string
    days?: string[]
    startTime?: string
    endTime?: string
    minTU?: number
  }>
}

export interface TUCapacitySignal {
  dimension: string
  usage?: string | number
  requiredTU?: number
  fresh?: boolean
}

export interface TUCapacityStatus {
  profileRef?: string
  allocatedTU?: number
  desiredTU?: number
  effectiveMinTU?: number
  dominantDimension?: string
  atMaxTU?: boolean
  activeSchedules?: string[]
  signals?: TUCapacitySignal[]
  lastDecisionTime?: string
}

export interface TUCapacityProfileDimension {
  capacityPerTU: number
  targetUtilization: number
  unit: 'MBps' | 'entriesPerSecond' | 'resources'
}

export interface TUCapacityDimensionState extends TUCapacityProfileDimension {
  dimension: TUCapacityDimension
  usage: number
  requiredTU: number
  allocatedCapacity: number
  maxCapacity: number
  utilizationRatio: number
}

export interface TUBasedSizingEligibility {
  isByoc: boolean
  isEdit: boolean
  resourceType: string
  clusterProfile: string
  hasTUCapacity?: boolean
}

export const TU_CAPACITY_PROFILE: Record<TUCapacityDimension, TUCapacityProfileDimension> = {
  ingress: {
    capacityPerTU: 25,
    targetUtilization: 0.75,
    unit: 'MBps'
  },
  egress: {
    capacityPerTU: 75,
    targetUtilization: 0.75,
    unit: 'MBps'
  },
  requestEntries: {
    capacityPerTU: 2500,
    targetUtilization: 0.75,
    unit: 'entriesPerSecond'
  },
  topicsOrPartitions: {
    capacityPerTU: 5000,
    targetUtilization: 0.8,
    unit: 'resources'
  },
  clientSessions: {
    capacityPerTU: 20000,
    targetUtilization: 0.75,
    unit: 'resources'
  }
}

export const TU_CAPACITY_RANGE = {
  min: 1,
  max: 20
} as const

export const BYOC_ETU_LIST_PRICE = {
  hourlyRate: 0.5,
  monthlyHours: 730
} as const

export const DEFAULT_TU_USAGE: TUCapacityUsage = {
  ingress: 68,
  egress: 148,
  requestEntries: 24000,
  topicsOrPartitions: 44000,
  clientSessions: 14900
}

export const shouldUseTUBasedSizing = ({
  isByoc,
  isEdit,
  resourceType,
  clusterProfile,
  hasTUCapacity = false
}: TUBasedSizingEligibility) =>
  isByoc &&
  resourceType === 'pulsar' &&
  clusterProfile !== 'cost-optimized' &&
  (!isEdit || hasTUCapacity)

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const calculateRequiredTU = (usage: number, dimension: TUCapacityDimension) => {
  const profile = TU_CAPACITY_PROFILE[dimension]
  const targetCapacity = profile.capacityPerTU * profile.targetUtilization
  return Math.max(1, Math.ceil(usage / targetCapacity))
}

export const calculateTUCapacityState = ({
  usage,
  minTU,
  maxTU,
  scheduledMinTU = 0
}: {
  usage: TUCapacityUsage
  minTU: number
  maxTU: number
  scheduledMinTU?: number
}) => {
  const states = (Object.keys(TU_CAPACITY_PROFILE) as TUCapacityDimension[]).map(dimension => {
    const profile = TU_CAPACITY_PROFILE[dimension]
    const requiredTU = calculateRequiredTU(usage[dimension], dimension)

    return {
      dimension,
      usage: usage[dimension],
      requiredTU,
      allocatedCapacity: profile.capacityPerTU * clamp(requiredTU, minTU, maxTU),
      maxCapacity: profile.capacityPerTU * maxTU,
      utilizationRatio: usage[dimension] / (profile.capacityPerTU * maxTU),
      ...profile
    } satisfies TUCapacityDimensionState
  })

  const dominant = states.reduce((current, candidate) =>
    candidate.requiredTU > current.requiredTU ? candidate : current
  )
  const demandTU = dominant.requiredTU
  const effectiveMinTU = Math.max(minTU, scheduledMinTU)
  const desiredTU = clamp(Math.max(effectiveMinTU, demandTU), effectiveMinTU, maxTU)

  return {
    states,
    dominantDimension: dominant.dimension,
    demandTU,
    desiredTU,
    effectiveMinTU,
    atMax: demandTU >= maxTU
  }
}

export const getCapacityForTU = (tu: number) => ({
  ingress: TU_CAPACITY_PROFILE.ingress.capacityPerTU * tu,
  egress: TU_CAPACITY_PROFILE.egress.capacityPerTU * tu,
  requestEntries: TU_CAPACITY_PROFILE.requestEntries.capacityPerTU * tu,
  topicsOrPartitions: TU_CAPACITY_PROFILE.topicsOrPartitions.capacityPerTU * tu,
  clientSessions: TU_CAPACITY_PROFILE.clientSessions.capacityPerTU * tu
})

export const parseMetricQuantity = (value: string | number | undefined) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (!value) return undefined

  const match = value.trim().match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))([a-zA-Z]*)$/)
  if (!match) return undefined

  const number = Number(match[1])
  const multipliers: Record<string, number> = {
    '': 1,
    n: 1e-9,
    u: 1e-6,
    m: 1e-3,
    k: 1e3,
    K: 1e3,
    M: 1e6,
    G: 1e9,
    T: 1e12,
    Ki: 1024,
    Mi: 1024 ** 2,
    Gi: 1024 ** 3,
    Ti: 1024 ** 4
  }
  const multiplier = multipliers[match[2]]
  return multiplier === undefined ? undefined : number * multiplier
}

export const isTUCapacitySpec = (capacity?: TUCapacitySpec) =>
  capacity?.mode?.toUpperCase() === 'TU'

export const calculateMonthlyETUListCost = (tu: number) =>
  tu * BYOC_ETU_LIST_PRICE.hourlyRate * BYOC_ETU_LIST_PRICE.monthlyHours

export const formatTUCapacityValue = (
  value: number,
  unit: 'MBps' | 'entriesPerSecond' | 'resources'
) => {
  if (unit === 'MBps') {
    return `${value.toLocaleString()} MB/s`
  }

  const formattedValue = (() => {
    if (value >= 1000) {
      const compactValue = value % 1000 === 0 ? value / 1000 : (value / 1000).toFixed(1)
      return `${compactValue}k`
    }
    return value.toLocaleString()
  })()

  if (unit === 'entriesPerSecond') {
    return `${formattedValue} entries/s`
  }

  return formattedValue
}

export const useTUCapacityModel = () => ({
  profile: TU_CAPACITY_PROFILE,
  range: TU_CAPACITY_RANGE,
  defaultUsage: DEFAULT_TU_USAGE,
  calculateState: calculateTUCapacityState,
  getCapacityForTU,
  calculateMonthlyListCost: calculateMonthlyETUListCost,
  listPrice: BYOC_ETU_LIST_PRICE,
  formatValue: formatTUCapacityValue
})
